<template>
    <div id="historical-distributions">
        <div class="distribution-tab-nav">
            <div class="historical-distribution-tab tab">
                <h3 class="header">
                    <i class="icon icon-down-carrot" />
                    Historical Distributions
                </h3>
                <span v-if="asOfDate !== '--'">as of {{ asOfDate }}</span>
            </div>
            <!--Historical distributions link, hiding for now (CG-17099)-->
            <!-- <a class="historical-distributions-link large" :href="historicalDistributionsLink" v-if="isTargetDate">
            <div class="arrow-wrapper">
                <i class="icon icon-arrow-right" />
            </div>
            FIND HISTORICAL DISTRIBUTIONS
			</a>-->
        </div>
        <div class="distributions-year-nav">
            <button @click="updateSelectedYear(selectedYear - 1)">
                <i class="icon icon-left-carrot" />
            </button>
            <div class="selected-year">{{ selectedYear }}</div>
            <button @click="updateSelectedYear(selectedYear + 1)">
                <i class="icon icon-right-carrot" />
            </button>
        </div>
        <div id="table-wrapper">
            <table>
                <tbody>
                    <tr>
                        <th class="text-align-left">Record Date</th>
                        <th class="text-align-left">Calculated Date</th>
                        <th class="text-align-left">Pay Date</th>
                        <th v-if="consolidateColumns" colspan="2">
                            Income Dividend Regular and Special
                            <i-ball-modal tag="NON_DAILY_DIV" />
                        </th>
                        <template v-else>
                            <th>
                                Income Dividend Regular
                                <i-ball-modal tag="NON_DAILY_DIV" />
                            </th>
                            <th>
                                Income Dividend Special
                                <i-ball-modal tag="NON_DAILY_SP_DIV" />
                            </th>
                        </template>
                        <th>
                            Cap. Gains Long-Term
                            <i-ball-modal tag="CAP_GAIN_PAID" />
                        </th>
                        <th>
                            Cap. Gains Short-Term
                            <i-ball-modal tag="ST_CAP_GAIN_PAID" />
                        </th>
                        <th class="text-align-right">Reinvest NAV</th>
                    </tr>
                    <tr v-if="!hasData && !hasYTDData">
                        <td colspan="8" class="no-data-banner">No data available for {{ selectedYear }}</td>
                    </tr>
                    <tr v-for="(distributions, index) in distributionsForSelectedYear" :key="index">
                        <td class="text-align-left">{{ distributions.ExDate || '--' }}</td>
                        <td class="text-align-left">{{ distributions.ReinvestDate || '--' }}</td>
                        <td class="border-right text-align-left">{{ distributions.CheckDate || '--' }}</td>
                        <td v-if="consolidateColumns" class="border-right" colspan="2">{{
                            this.$format.histDistPrice(consolidatedDistributions(distributions.RegularDividendAmount,
                            distributions.SpecialDividendAmount)) }}</td>
                        <template v-else>
                            <td>{{ this.$format.histDistPrice(distributions.RegularDividendAmount) }}</td>
                            <td class="border-right">{{ this.$format.histDistPrice(distributions.SpecialDividendAmount) }}
                            </td>
                        </template>
                        <td>{{ this.$format.histDistPrice(distributions.LongTermCapitalGainsAmount) }}</td>
                        <td class="border-right">{{ this.$format.histDistPrice(distributions.ShortTermCapitalGainsAmount) }}
                        </td>
                        <td class="text-align-right">{{ this.$format.histDistPrice(distributions.ReinvestPrice) }}</td>
                    </tr>
                    <tr>
                        <td colspan="3" class="border-right text-align-right padding-right-10">Year-to-Date:</td>
                        <td colspan="2" class="border-right">Dividends Subtotal: {{ dividendSubtotal }}</td>
                        <td colspan="2" class="border-right">Cap Gains Subtotal: {{ capGainsSubtotal }}</td>
                        <td />
                    </tr>
                    <tr>
                        <td colspan="3" class="border-right" />
                        <td colspan="4" class="border-right">Total Distributions: {{ totalDistributions }}</td>
                        <td />
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'HistoricalDistributions',
    data() {
        return {
            shouldDefaultYear: true,
            selectedYear: new Date().getFullYear()
        };
    },
    computed: {
        ...mapGetters([
            'historicalDistributionsData',
            'getAudience',
            'getFundAttributes'
        ]),
        widgetData() {
            const data = {};
            if (this.getFundAttributes) {
                data.objective = this.getFundAttributes.Objective;
                data.shareClass = this.getFundAttributes.ShareClass;
            }
            if (this.historicalDistributionsData && this.shouldDefaultYear) {
                //default to themost recent year that has distributions on initial load
                data.distributions = this.defaultToMostRecentYearWithData(
                    this.historicalDistributionsData
                );
                data.yearToDate = this.historicalDistributionsData.yearToDate;
            } else if (this.historicalDistributionsData) {
                data.distributions = this.historicalDistributionsData.distributions[
                    this.selectedYear
                ];
                data.yearToDate = this.historicalDistributionsData.yearToDate;
            }
            this.$EventBus.$emit(
                'ExcelData',
                'HistoricalDistributionsModule',
                data
            );
            return data;
        },
        lastYearWithData() {
            let result = Object.keys(this.YTDDistributions)
                .sort()
                .reverse()
                .find(year => {
                    return this.YTDDistributions[year].PricingAsOfDate !== null
                        && this.YTDDistributions[year].PricingAsOfDate !== '--';
                })
                || null;
            return result;
        },
        consolidateColumns() {
            return !!(
                this.widgetData.objective === 'ManagedRisk' ||
                (this.widgetData.shareClass &&
                    this.widgetData.shareClass.toLowerCase() === 'p2')
            );
        },
        distributionsForSelectedYear() {
            return this.widgetData.distributions || [];
        },
        YTDDistributions() {
            return this.widgetData.yearToDate || {};
        },
        asOfDate() {
            return (
                (this.YTDDistributions[this.lastYearWithData] &&
                    this.YTDDistributions[this.lastYearWithData]
                        .PricingAsOfDate) ||
                '--'
            );
        },
        hasData() {
            return !!this.distributionsForSelectedYear.length;
        },
        hasYTDData() {
            return this.totalDistributions !== '--';
        },
        isTargetDate() {
            return this.widgetData.objective === 'RetirementTargetDate';
        },
        dividendSubtotal() {
            const YTDDividendSubtotal =
                this.widgetData.yearToDate &&
                this.widgetData.yearToDate[this.selectedYear] &&
                this.widgetData.yearToDate[this.selectedYear]
                    .YTDDividendsAmount;
            return (
                ((YTDDividendSubtotal || YTDDividendSubtotal == 0) &&
                    this.$format.histDistPrice(YTDDividendSubtotal)) ||
                '--'
            );
        },
        capGainsSubtotal() {
            const YTDCapitalGainsAmount =
                this.widgetData.yearToDate &&
                this.widgetData.yearToDate[this.selectedYear] &&
                this.widgetData.yearToDate[this.selectedYear]
                    .YTDCapitalGainsAmount;
            return (
                ((YTDCapitalGainsAmount || YTDCapitalGainsAmount == 0) &&
                    this.$format.histDistPrice(YTDCapitalGainsAmount)) ||
                '--'
            );
        },
        totalDistributions() {
            const YTDTotalDistributionsAmount =
                this.widgetData.yearToDate &&
                this.widgetData.yearToDate[this.selectedYear] &&
                this.widgetData.yearToDate[this.selectedYear]
                    .YTDTotalDistributionsAmount;
            return (
                ((YTDTotalDistributionsAmount ||
                    YTDTotalDistributionsAmount == 0) &&
                    this.$format.histDistPrice(YTDTotalDistributionsAmount)) ||
                '--'
            );
        }
    },
    mounted() {
		this.setHorizontalScrollOnTable();
	},
    watch: {
        widgetData() {
            if (this.shouldDefaultYear) {
                this.shouldDefaultYear = false;
            }
        }
    },
    methods: {
        defaultToMostRecentYearWithData(data) {
            while (
                !data.distributions[this.selectedYear] &&
                !data.yearToDate[this.selectedYear] &&
                this.selectedYear > new Date().getFullYear() - 100
            ) {
                this.selectedYear -= 1;
            }
            if (
                !data.distributions[this.selectedYear] &&
                !data.yearToDate[this.selectedYear]
            ) {
                this.selectedYear = new Date().getFullYear();
            }
            return data.distributions[this.selectedYear];
        },
        updateSelectedYear(year) {
            this.selectedYear = year;
        },
		consolidatedDistributions(
			RegularDividendAmount,
			SpecialDividendAmount
		) {
			const regularDividend = isNaN(RegularDividendAmount)
				? 0
				: Number(RegularDividendAmount);
			const specialDividend = isNaN(SpecialDividendAmount)
				? 0
				: Number(SpecialDividendAmount);
			return regularDividend + specialDividend;
		},
		setHorizontalScrollOnTable() {
			const slider = document.querySelector('#table-wrapper');
			let isDown = false;
			let startX;
			let scrollLeft;
			slider.addEventListener('mousedown', e => {
				e.preventDefault();
				isDown = true;
				startX = e.pageX - slider.offsetLeft;
				scrollLeft = slider.scrollLeft;
			});
			document.addEventListener('mouseup', () => {
				isDown = false;
			});
			document.addEventListener('mousemove', e => {
				if (!isDown) return;
				e.preventDefault();
				const x = e.pageX - slider.offsetLeft;
				const walk = x - startX;
				slider.scrollLeft = scrollLeft - walk;
			});
		}
    }
};
</script>

<style lang="less" scoped>
#historical-distributions {
    .bottom-anchor-palcement {
        display: none;
        margin-top: 15px;
        margin-left: auto;
        margin-bottom: auto;
        text-transform: uppercase;
        font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;

        .arrow-wrapper {
            display: inline-block;
            overflow: hidden;

            .arrow {
                color: #878a96;
                font-size: 28px;
                margin-left: -20px;
                line-height: 15px;
            }
        }
    }

    .distribution-tab-nav {
        border-top: 1px solid rgb(212, 212, 212);
        margin-bottom: 30px;
        display: flex;

        .historical-distribution-tab {
            display: inline-block;
            background-color: #e5e5e5;
            background-image: linear-gradient(to bottom, #e5e5e5, #fff);
            color: #000000;
            height: 80px;
            width: 247px;
            padding: 20px 10px;

            .icon {
                display: none;
            }

            h3 {
                width: 100%;
            }

            span {
                font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
                font-size: 14px;
            }
        }
    }

    .distributions-year-nav {
        height: 38px;
        padding-top: 5px;
        padding-bottom: 5px;
        display: flex;
        border-top: 1px solid rgb(212, 212, 212);
        border-bottom: 1px solid rgb(212, 212, 212);
        background-color: #f5f5f5;

        button {
            width: 26px;
            height: 26px;
            background-color: white;
            color: #52bdec;
            cursor: pointer;
            margin: 0px 30px;
            border-radius: 3px;
            border-width: 1px;
            border-style: solid;
            border-color: #d4d4d4;
            border-image: initial;
            font-weight: 100;
            font-size: 12px;
            line-height: 0;

            &:focus {
                outline: none;
            }
        }

        .selected-year {
            font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
            display: inline-block;
            text-align: center;
            line-height: 26px;
            flex: 1;
        }
    }

    #table-wrapper {
        overflow: hidden;
        overflow-x: scroll;
        margin-bottom: 15px;

        &::-webkit-scrollbar {
            display: none;
            width: 0px;
            /* Remove scrollbar space */
            background: transparent;
            /* Optional: just make scrollbar invisible */
        }

        table {
            width: 970px;
            font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
            font-size: 14px;
            color: #333;
            line-height: 20px;

            tr td {
                border-bottom: 1px solid #d4d4d4;
            }

            td,
            th {
                text-align: center;
            }

            tr:first-of-type {
                color: #666;
            }

            tr:nth-child(2) td {
                border-top: 1px solid #d4d4d4;
            }

            tr:nth-last-of-type(2) {
                td {
                    &:nth-child(1) {
                        width: 29.793%;
                    }

                    &:nth-child(2) {
                        width: 24.226%;
                    }

                    &:nth-child(3) {
                        width: 36.701%;
                    }

                    &:nth-child(4) {
                        width: 9.278%;
                    }
                }

                td:last-of-type {
                    border-bottom: none;
                }
            }

            tr {
                td {
                    &.no-data-banner {
                        text-align: center;
                        background-color: #f5f5f5;
                        line-height: 24px;
                    }
                }

                .padding-right-10 {
                    padding-right: 10px;
                }

                .text-align-left {
                    text-align: left;
                }

                .text-align-right {
                    text-align: right;
                }

                .border-right {
                    border-right: 1px solid #d4d4d4;
                }
            }

            tr:nth-last-of-type(1),
            tr:nth-last-of-type(2) {
                td {
                    padding-top: 8px;
                    padding-bottom: 8px;
                }
            }
        }
    }

    .historical-distributions-link {
        color: #1d5799;
        display: block;
        font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
        font-size: 14px;
        margin-top: 10px;
        margin-left: auto;
        margin-bottom: 30px;
        text-transform: uppercase;

        .arrow-wrapper {
            display: inline-block;
            color: #878a96;

            .icon {
                line-height: 15px;
            }
        }

        &.large {
            float: right;
        }
    }
}

@media (max-width: 989px) {
    #historical-distributions {
        #table-wrapper {
            padding: 0 8px;
            background-image: linear-gradient(to right,
                    rgba(0, 0, 0, 0.3),
                    transparent 1%,
                    transparent 99%,
                    rgba(0, 0, 0, 0.3));
        }
    }
}

@media (max-width: 768px) {
    #historical-distributions {
        .distribution-tab-nav {
            display: block;

            .historical-distribution-tab {
                display: block;
                width: 100%;
                height: auto;
                min-height: 51px;
                background-color: #999;
                background-image: none;
                color: #fff;
                padding: 13px 10px;

                .icon {
                    display: initial;
                }

                h3,
                span {
                    width: auto;
                    display: inline-block;
                    color: #fff;
                    font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
                    font-size: 18px;
                }
            }

            a {
                display: none;
            }
        }

        .bottom-anchor-palcement {
            display: initial;
            margin-top: 10px;
        }
    }
}
</style>